import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageLoader, LoadingSpinner } from '../components/common/Loading';
import { AuthContext } from '../context/AuthContext';
import { WalletAPI, APIUtils } from '../services/api';
import { 
  FaBitcoin, 
  FaUpload, 
  FaCopy, 
  FaCheckCircle, 
  FaClock, 
  FaTimesCircle, 
  FaArrowLeft, 
  FaInfoCircle, 
  FaExternalLinkAlt,
  FaImage,
  FaFilePdf,
  FaTimes
} from 'react-icons/fa';
import { FiAlertCircle } from 'react-icons/fi';

const DepositPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [depositInfo, setDepositInfo] = useState(null);
  const [depositHistory, setDepositHistory] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    tx_hash: '',
    screenshot: null,
    screenshot_preview: null,
    screenshot_name: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('deposit');

  // Fetch deposit info on load
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDepositInfo();
    fetchDepositHistory();
  }, [user, navigate]);

  const fetchDepositInfo = async () => {
    try {
      setLoading(true);
      const response = await WalletAPI.getDepositAddress();
      if (response && response.success !== false) {
        setDepositInfo(response);
      } else {
        setErrors(prev => ({ 
          ...prev, 
          submit: response?.error || 'Failed to load deposit information' 
        }));
      }
    } catch (error) {
      // console.error('Error fetching deposit info:', error);
      setErrors(prev => ({ 
        ...prev, 
        submit: 'Failed to load deposit information. Please try again.' 
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchDepositHistory = async () => {
    try {
      const response = await WalletAPI.getUserDepositProofs();
      if (response && response.success !== false) {
        setDepositHistory(response.deposits || []);
      }
    } catch (error) {
      // console.error('Error fetching deposit history:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, screenshot: 'Please upload a JPG, PNG, GIF, or PDF file' }));
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, screenshot: 'File size must be less than 5MB' }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        screenshot: file,
        screenshot_preview: URL.createObjectURL(file),
        screenshot_name: file.name
      }));
      setErrors(prev => ({ ...prev, screenshot: '' }));
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      screenshot: null,
      screenshot_preview: null,
      screenshot_name: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const newErrors = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    if (!formData.screenshot) {
      newErrors.screenshot = 'Please upload proof of payment';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setSubmitting(true);
    setErrors(prev => ({ ...prev, submit: '' }));
    
    try {
      // First upload screenshot if exists
      let screenshotPath = '';
      if (formData.screenshot) {
        const uploadData = new FormData();
        uploadData.append('screenshot', formData.screenshot);
        
        const uploadResponse = await WalletAPI.uploadDepositScreenshot(formData.screenshot);
        if (uploadResponse && uploadResponse.success !== false) {
          screenshotPath = uploadResponse.screenshot_path;
        } else {
          throw new Error(uploadResponse?.error || 'Failed to upload screenshot');
        }
      }
      
      // Submit deposit proof
      const depositData = {
        amount: parseFloat(formData.amount),
        tx_hash: formData.tx_hash || '',
        screenshot: screenshotPath
      };
      
      const response = await WalletAPI.submitDepositProof(depositData);
      
      if (response && response.success !== false) {
        setSuccessMessage('✅ Deposit proof submitted successfully! Our team will verify and credit your account shortly.');
        setFormData({
          amount: '',
          tx_hash: '',
          screenshot: null,
          screenshot_preview: null,
          screenshot_name: ''
        });
        fetchDepositHistory();
        // Clear success message after 10 seconds
        setTimeout(() => setSuccessMessage(''), 10000);
      } else {
        throw new Error(response?.error || 'Failed to submit deposit proof');
      }
    } catch (error) {
      // console.error('Submit deposit error:', error);
      setErrors(prev => ({ ...prev, submit: error.message || 'Failed to submit deposit proof' }));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Address copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: <FaClock className="w-4 h-4" />, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Pending' },
      completed: { icon: <FaCheckCircle className="w-4 h-4" />, color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed' },
      rejected: { icon: <FaTimesCircle className="w-4 h-4" />, color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Rejected' }
    };
    const configItem = config[status] || config.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${configItem.color} flex items-center gap-1.5 inline-flex`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <PageLoader text="Loading deposit information..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/wallet')}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">💎 Deposit Bitcoin</h1>
            <p className="text-gray-400 text-sm">Send BTC and upload proof of payment</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('deposit')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'deposit'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaBitcoin className="inline mr-2" />
            Deposit
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaClock className="inline mr-2" />
            History
          </button>
        </div>

        {activeTab === 'deposit' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Deposit Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">Submit Deposit Proof</h3>

                {/* Success Message */}
                {successMessage && (
                  <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <p className="text-green-400 text-sm flex items-center gap-2">
                      <FaCheckCircle className="w-5 h-5" />
                      {successMessage}
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {errors.submit && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <FiAlertCircle className="w-5 h-5" />
                      {errors.submit}
                    </p>
                  </div>
                )}

                {/* Deposit Address Display */}
                <div className="bg-gray-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-300">System Deposit Address</label>
                    {depositInfo?.network && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        depositInfo.network === 'mainnet' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {depositInfo.network}
                      </span>
                    )}
                  </div>
                  <div className="bg-black/50 rounded-lg p-3 mb-3 flex items-center gap-2">
                    <code className="text-yellow-400 text-sm break-all flex-1 font-mono">
                      {depositInfo?.address || 'No address configured'}
                    </code>
                    {depositInfo?.address && (
                      <button
                        onClick={() => copyToClipboard(depositInfo.address)}
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                        title="Copy address"
                      >
                        <FaCopy className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                  {depositInfo?.address && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyToClipboard(depositInfo.address)}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                      >
                        Copy Address
                      </button>
                      <a
                        href={`https://www.blockchain.com/explorer/addresses/btc/${depositInfo.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <FaExternalLinkAlt className="w-3 h-3" />
                        View Explorer
                      </a>
                    </div>
                  )}
                </div>

                {/* Deposit Instructions */}
                {depositInfo?.instructions && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
                    <h4 className="text-blue-400 font-medium flex items-center gap-2 mb-2">
                      <FaInfoCircle className="w-4 h-4" />
                      Instructions
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-300">
                      {depositInfo.instructions.map((instruction, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-blue-400">•</span>
                          {instruction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Amount (BTC) *
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      step="0.00000001"
                      min="0.0001"
                      placeholder="0.00000000"
                      className={`w-full px-4 py-3 bg-gray-800 text-white rounded-lg border ${
                        errors.amount ? 'border-red-500' : 'border-gray-700'
                      } focus:border-blue-500 focus:outline-none`}
                    />
                    {errors.amount && (
                      <p className="text-red-400 text-sm mt-1">{errors.amount}</p>
                    )}
                    {depositInfo?.min_deposit && (
                      <p className="text-gray-400 text-xs mt-1">
                        Minimum: {depositInfo.min_deposit} BTC
                      </p>
                    )}
                  </div>

                  {/* Transaction Hash (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Transaction Hash (optional)
                    </label>
                    <input
                      type="text"
                      name="tx_hash"
                      value={formData.tx_hash}
                      onChange={handleInputChange}
                      placeholder="e.g., 1234abc... (optional)"
                      className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none font-mono"
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      You can provide the transaction hash for faster verification
                    </p>
                  </div>

                  {/* Screenshot Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Proof of Payment (Screenshot) *
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      errors.screenshot ? 'border-red-500' : 'border-gray-700'
                    } hover:border-blue-500 transition-colors`}>
                      {formData.screenshot_preview ? (
                        <div className="space-y-3">
                          <div className="relative">
                            {formData.screenshot.type === 'application/pdf' ? (
                              <div className="flex items-center justify-center gap-2 p-4 bg-gray-800 rounded-lg">
                                <FaFilePdf className="w-12 h-12 text-red-400" />
                                <div className="text-left">
                                  <p className="text-white font-medium">{formData.screenshot_name}</p>
                                  <p className="text-gray-400 text-sm">
                                    {(formData.screenshot.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={formData.screenshot_preview}
                                alt="Screenshot preview"
                                className="max-h-48 mx-auto rounded-lg object-contain"
                              />
                            )}
                            <button
                              type="button"
                              onClick={handleRemoveFile}
                              className="absolute -top-2 -right-2 p-1 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                document.getElementById('screenshot-input').click();
                              }}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                            >
                              Change File
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <FaImage className="w-12 h-12 text-gray-600 mx-auto" />
                          <div>
                            <p className="text-gray-300">Drag and drop or click to upload</p>
                            <p className="text-gray-500 text-sm">JPG, PNG, GIF, PDF (Max 5MB)</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => document.getElementById('screenshot-input').click()}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                          >
                            <FaUpload className="inline mr-2" />
                            Choose File
                          </button>
                        </div>
                      )}
                      <input
                        id="screenshot-input"
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                    {errors.screenshot && (
                      <p className="text-red-400 text-sm mt-1">{errors.screenshot}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !depositInfo?.address}
                    className={`w-full py-3 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                      submitting || !depositInfo?.address
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-lg hover:shadow-blue-500/30'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" color="white" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="w-5 h-5" />
                        Submit Deposit Proof
                      </>
                    )}
                  </button>

                  {!depositInfo?.address && (
                    <p className="text-yellow-400 text-sm text-center">
                      ⚠️ System deposit address is not configured. Please contact support.
                    </p>
                  )}
                </form>
              </div>
            </div>

            {/* Right Column - Quick Info */}
            <div className="space-y-6">
              {/* Quick Tips */}
              <div className="bg-gray-900 rounded-xl p-6">
                <h4 className="font-bold text-white mb-4">📌 Quick Tips</h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 mt-1">1.</span>
                    <span className="text-gray-300">Send the exact amount you enter to the deposit address</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 mt-1">2.</span>
                    <span className="text-gray-300">Take a screenshot of the transaction confirmation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 mt-1">3.</span>
                    <span className="text-gray-300">Upload the screenshot and submit the form</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-400 mt-1">4.</span>
                    <span className="text-gray-300">Wait for admin verification (usually within 24 hours)</span>
                  </li>
                </ul>
              </div>

              {/* Minimum Requirements */}
              <div className="bg-gray-900 rounded-xl p-6">
                <h4 className="font-bold text-white mb-4">📋 Requirements</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Min Deposit:</span>
                    <span className="text-white">{depositInfo?.min_deposit || 0.0001} BTC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Confirmations:</span>
                    <span className="text-white">{depositInfo?.required_confirmations || 3}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Network:</span>
                    <span className="text-white">{depositInfo?.network || 'testnet'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Processing Time:</span>
                    <span className="text-white">~24 hours</span>
                  </div>
                </div>
              </div>

              {/* Back to Wallet */}
              <button
                onClick={() => navigate('/wallet')}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="w-4 h-4" />
                Back to Wallet
              </button>
            </div>
          </div>
        ) : (
          // History Tab
          <div className="bg-gray-900 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-6">📊 Deposit History</h3>
            
            {depositHistory.length === 0 ? (
              <div className="text-center py-12">
                <FaClock className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400">No deposit history found</p>
                <p className="text-gray-500 text-sm mt-2">Your deposit submissions will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {depositHistory.map((deposit) => (
                  <div key={deposit.id} className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-white">
                            {parseFloat(deposit.amount).toFixed(8)} BTC
                          </span>
                          {getStatusBadge(deposit.status)}
                        </div>
                        <div className="text-sm text-gray-400 space-y-1">
                          {deposit.tx_hash && (
                            <div className="flex items-center gap-2">
                              <span>TX:</span>
                              <code className="text-xs bg-black/30 px-2 py-1 rounded font-mono">
                                {deposit.tx_hash.substring(0, 16)}...
                              </code>
                              <button
                                onClick={() => copyToClipboard(deposit.tx_hash)}
                                className="text-gray-500 hover:text-white transition-colors"
                              >
                                <FaCopy className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-4">
                            <span>Submitted: {new Date(deposit.created_at).toLocaleString()}</span>
                            {deposit.processed_at && (
                              <span className="text-green-400">
                                Processed: {new Date(deposit.processed_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {deposit.screenshot_path && (
                        <a
                          href={`https://api.diceace21.com/${deposit.screenshot_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors flex items-center gap-2"
                        >
                          <FaImage className="w-4 h-4" />
                          View Proof
                        </a>
                      )}
                    </div>
                    {deposit.admin_notes && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-sm text-gray-400">
                          <span className="font-medium">Admin Note:</span> {deposit.admin_notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DepositPage;